import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { conclaveApi, type ConclaveSeatingOverview } from '@/lib/conclave-api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

function errorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error && 'response' in error) {
    const data = (error as { response?: { data?: { error?: string } } }).response?.data;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function ConclaveChangeTableSheet({
  open,
  onOpenChange,
  eventId,
  sessionId,
  revision,
  onMoved,
  lockedMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  sessionId: string | null;
  revision: number;
  onMoved: (tableNumber: number) => void;
  lockedMessage?: string | null;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="mx-auto flex h-[85dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border-t px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-6 sm:px-6"
      >
        <SheetHeader className="shrink-0 pr-8 text-left">
          <SheetTitle>Change table</SheetTitle>
          <SheetDescription>
            {lockedMessage ??
              'Pick a table to move to. Your new seat shows on the organiser board.'}
          </SheetDescription>
        </SheetHeader>
        {open && !lockedMessage && sessionId ? (
          <ChangeTableList
            eventId={eventId}
            sessionId={sessionId}
            revision={revision}
            onMoved={onMoved}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function ChangeTableList({
  eventId,
  sessionId,
  revision,
  onMoved,
}: {
  eventId: string;
  sessionId: string;
  revision: number;
  onMoved: (tableNumber: number) => void;
}) {
  const [overview, setOverview] = useState<ConclaveSeatingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [movingTo, setMovingTo] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void conclaveApi
      .getSeatingOverview(eventId, sessionId)
      .then((data) => {
        if (!cancelled) setOverview(data);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setOverview(null);
          toast.error(errorMessage(error, 'Could not load tables'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, sessionId, revision]);

  async function sitAt(tableNumber: number) {
    if (!overview || movingTo != null || overview.myTableNumber === tableNumber) return;
    setMovingTo(tableNumber);
    try {
      await conclaveApi.changeMyTable(eventId, { sessionId, tableNumber });
      toast.success(`You're now at table ${tableNumber}`);
      onMoved(tableNumber);
    } catch (error: unknown) {
      toast.error(errorMessage(error, 'Could not change table'));
      setMovingTo(null);
    }
  }

  const tables = overview?.tables ?? [];
  const seatsMax = overview?.seatsMax ?? null;

  return (
    <div className="mt-4 min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-2">
      {loading && !overview ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : tables.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
          No tables yet.
        </p>
      ) : (
        tables.map((table) => {
          const isCurrent = overview?.myTableNumber === table.tableNumber;
          const full = seatsMax != null && table.people.length >= seatsMax;
          const overCapacity = seatsMax != null && table.people.length > seatsMax;
          const moving = movingTo === table.tableNumber;
          return (
            <div
              key={table.tableNumber}
              className={cn(
                'rounded-2xl border bg-card p-3 shadow-sm',
                isCurrent ? 'border-primary bg-primary/5' : 'border-border'
              )}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-full border-2 border-primary/40 bg-gradient-to-br from-primary/15 to-primary/5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                    Table
                  </span>
                  <span className="text-lg font-bold leading-none text-primary">
                    {table.tableNumber}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-semibold">{table.people.length} seated</span>
                    {isCurrent ? (
                      <span className="whitespace-nowrap rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                        Your table
                      </span>
                    ) : null}
                    {full ? (
                      <span className="whitespace-nowrap rounded-full bg-red-100 px-2 py-0.5 text-xs font-normal text-red-800">
                        Full
                      </span>
                    ) : null}
                    {overCapacity ? (
                      <span className="whitespace-nowrap rounded-full bg-red-100 px-2 py-0.5 text-xs font-normal text-red-800">
                        Over capacity
                      </span>
                    ) : null}
                  </div>
                </div>
                {isCurrent ? null : (
                  <Button
                    type="button"
                    size="sm"
                    className="shrink-0 rounded-xl"
                    disabled={movingTo != null}
                    onClick={() => void sitAt(table.tableNumber)}
                  >
                    {moving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Sit here
                  </Button>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
